
import AppProductSkuAutocomplete from "@/components/products/ProductSkuAutocomplete.vue";
import AppVatSelect from "@/components/SelectVat.vue";
import Duration from "@/components/common/DurationInput.vue";
import {Product, ProductCompositionRow} from "@/models/base";
import {VatService} from "@/services/vat-service";
import {roundTo8Decimals} from "@/utils/numbers";
import {DecimalConfig} from "@/models/dossier-configs/decimal-config";
import Vue from "vue";
import {DossierConfigService} from "@/services/dossier-config-service";
import {UserConfigService} from "@/services/user-config-service";
import AppUnitAutocomplete from "../products-table/UnitAutocomplete.vue";
import AppInputWithTextareaModal from "@/components/common/InputWithTextareaModal.vue";
import {TranslateResult} from "vue-i18n";
import {durationToProductQty} from "@/utils/duration";
import {ISelectItem} from "winfakt-vue-components";
import {printLongLocalDateTime} from "../../utils/pretty-print";
import { ProductCompositionEditRowState } from "@/utils/product-composition-edit-row-state";
import { CustomTranslation } from "@/utils/custom-translation";
import AppEditTranslatedNames from "./ProductEditTranslatedNames.vue";
import {ContactsConfig} from "@/models/dossier-configs/contacts";

export default Vue.extend({
	data(){
		return {
			translatedNamesModal: false
		};
	},
	props: {
		row: Object as () => ProductCompositionRow,
		vatRegime: Number,
		contactDiscount: Number,
		priceCategory: Number,
		lng: {
			type: String,
			default: "nl-be"
		},
		state: {
			type: Object as () => ProductCompositionEditRowState,
			required: true
		}
	},
	computed: {
		qtyActions(): ISelectItem<string>[] {
			return [
				{text: this.$ct("show-qty").toString(), value: "hideTimePicker"},
				{text: this.$ct("show-time").toString(), value: "showTimePicker"}
			];
		},
		decimalSettings(): DecimalConfig {
			return DossierConfigService.getDecimalSettings();
		},
		contactsConfig(): ContactsConfig {
			return DossierConfigService.getContactsConfig();
		},
		vatMultiplier(): number {
			return this.state.vatMultiplier;
		},
		vat(): number {
			return this.state.vat;;
		},
		autoFocusQty(): Boolean {
			return UserConfigService.getSaleSettings().NewSaleAutoFocusField == "qty";
		},
		autoFocusSku(): Boolean {
			return UserConfigService.getSaleSettings().NewSaleAutoFocusField == "sku";
		},
		hideUnits(): Boolean {
			return !UserConfigService.getSaleSettings().ShowUnits;
		},
		productSkuSm(): number {
			if (this.hideUnits) {
				return 8;
			}
			return 5;
		},
		productSkuLg(): number {
			if (this.hideUnits) {
				return 4;
			}
			return 2;
		},
		unitLabel(): string | TranslateResult {
			let name = DossierConfigService.getGeneralConfig().UnitFieldName;
			if (name) {
				return name;
			}
			return this.$ct("sales-row.Unit");
		},
		discountsDisabled(): boolean {
			if (this.row.Discount != 0) {
				return false;
			}
			if (!this.state.product) {
				return false;
			}
			return this.state.product.DiscountsDisabled;
		}
	},
	watch: {
		contactDiscount() {
			this.state.contactDiscount = this.contactDiscount || 0.0;
		},
		row: {
			immediate: true,
			handler(newRow: ProductCompositionRow) {
				this.state.translatedDescription = new CustomTranslation(newRow.TranslatedDescription.getJSON);
				this.state.productDescription = newRow.ProductDescription;
				this.state.amount = newRow.Amount;
				this.state.unitPrice = newRow.UnitPrice;
				if(newRow.ReferencedProduct && UserConfigService.getGeneralSettings().GetPurchasePriceFromProductWhenZero){
					this.state.unitBuyPrice = newRow.ReferencedProduct.BuyNettoPrice || 0;
				}
				this.state.discount = newRow.Discount;
				this.state.duration = newRow.Duration;
				//vat is managed by contact or product, this should not be changed on init
				//this.vat = parseFloat(newRow.Vat);
				this.state.netto = roundTo8Decimals(this.state.unitPrice * this.state.amount * (1 - (this.state.discount / 100)));
				this.state.bruto = roundTo8Decimals(this.state.netto * this.vatMultiplier);
				this.state.unit = newRow.UnitName;
				this.state.product = newRow.ReferencedProduct;
				if (this.vatRegime == 1) {
					this.state.vatItem = VatService.getVatByValue(newRow.Vat);
				} else if (this.vatRegime == 16) {
					this.state.vatItem = VatService.getVatByValue(6);
				}
			}
		},
		"state.amount"() {
			this.state.netto = roundTo8Decimals(this.state.unitPrice * this.state.amount * (1 - (this.state.discount / 100)));
			this.setBruto(roundTo8Decimals(this.state.netto * this.vatMultiplier));
		},
		vatMultiplier() {
			this.setBruto(roundTo8Decimals(this.state.netto * this.vatMultiplier));
		},
		"state.discount"() {
			if (this.state.discount == null) {
				this.state.discount = 0;
			}
			if (Number.isNaN(this.state.discount)) {
				this.state.discount = 0;
			}
			this.state.netto = roundTo8Decimals(this.state.unitPrice * this.state.amount * (1 - (this.state.discount / 100)));
			this.setBruto(roundTo8Decimals(this.state.netto * this.vatMultiplier));
		},
		vatRegime: {
			immediate: false,
			async handler() {
				await this.setVatByRegime();
			}
		}
	},
	methods: {
		onAmountBlur(){
			if (!this.$refs.unit) return;
			(this.$refs.unit as any).focus();
		},
		onDescriptionBlur(){
			if (!this.$refs.unitPrice) return;
			(this.$refs.unitPrice as any).focus();
		},
		onUnitPriceBlur(){
			if (!this.$refs.discount) return;
			(this.$refs.discount as any).focus();
		},
		onDiscountBlur(){
			if (this.vatRegime == 2){
				if (!this.$refs.netto) return;
				return (this.$refs.netto as any).focus();
			}
			if (!this.$refs.vatItems) return;
			(this.$refs.vatItems as any).focus();
		},
		onVatSelectBlur(){
			if (!this.$refs.netto) return;
			(this.$refs.netto as any).focus();
		},
		onNettoBlur(){
			if (!this.$refs.bruto)return;
			(this.$refs.netto as any).focus();
		},
		onBrutoBlur(){
			if (!this.$refs.buttonAdd)return;
			(this.$refs.buttonAdd as any).focus();
		},
		focusQty(){
			if (!this.$refs.qty) return;
			(this.$refs.qty as any).focus().focus();
		},
		trigger(method: string) {
			(this as any)[method]();
		},
		hideTimePicker() {
			this.state.toggleTimePicker = false;
		},
		showTimePicker() {
			this.state.toggleTimePicker = true;
		},
		setVatByRegime() {
			if (this.vatRegime == 1) {
				if (this.state.product && this.state.product.Vat && this.state.product.ID != 0) {
					this.state.vatItem = this.state.product.Vat;
					return;
				}
				this.state.vatItem = VatService.getVatByValue(this.row.Vat);
				return;
			}
			let vatRegime = VatService.getVatRegime(this.vatRegime);
			this.state.vatItem = VatService.getVatByValue(vatRegime.getSaleVat());
		},
		setNetto(value: number, preventUnitReset?: boolean) {
			this.state.setNetto(value, preventUnitReset);
		},
		setBruto(value: number) {
			this.state.setBruto(value);
		},
		setUnitPrice(value: number) {
			this.state.setUnitPrice(value);
		},
		setDuration(value: number) {
			this.state.setDuration(value);
		},
		async onProductSkuSelect(product: Product) {
			if(!product) return;
			await this.productSelected(product);
			(this.$refs.productDescription as any).focus();
		},
		productSelected(product: Product) {
			this.state.product = product;
			this.state.productDescription = product.TranslatedName.getTranslation(this.lng);
			this.state.translatedDescription = new CustomTranslation(product.TranslatedName.getJSON());

			this.setVatByRegime();

			if (product.Unit != null) {
				this.state.unit = product.Unit.Name.getTranslation(this.lng);
			}
			if(product.MainSupplier &&  UserConfigService.getGeneralSettings().GetPurchasePriceFromProductWhenZero){
				this.state.unitBuyPrice = product.MainSupplier.NettoPrice;
			}

			if (this.priceCategory != 0) {
				let price = (product as any)["SellPrice" + this.priceCategory];
				this.state.unitPrice = parseFloat(price);
				this.setNetto(0, true);
			} else {
				this.state.unitPrice = product.SellPrice1;
			}
			this.state.leeggoedTax = product.LeeggoedTax;

			this.$emit("product-selected", product);
		},
		onMultipleProductsSkuSelect(products: Product[]) {
			this.$emit("products-selected", products);
		},
		add(skipNewRow:Boolean = false) {
			this.row.UnitName = this.state.unit;
			if (this.state.product) {
				this.row.ProductSku = this.state.product.Sku || "";
			}
			this.row.ProductDescription = this.state.productDescription;
			this.row.TranslatedDescription = new CustomTranslation(this.state.translatedDescription.getJSON());
			this.row.Amount = this.state.amount;
			this.row.UnitPrice = this.state.unitPrice;
			this.row.Discount = this.state.discount;
			this.row.Vat = this.vat;
			this.row.Duration = this.state.duration;
			this.row.ReferencedProduct = this.state.product;
			this.row.UnitBuyPrice = this.state.unitBuyPrice;
			if (this.state.duration) {
				this.row.Amount = durationToProductQty(this.state.duration);
			}
			this.state.toggleTimePicker = false;
			this.$emit("add", {row: this.row, product: this.state.product, skipNewRow});
			setTimeout(() => {
				let el = this.$refs.qty as HTMLInputElement;
				if (skipNewRow) {
					el = this.$refs.unitPrice as HTMLInputElement;
				}
				if (el) {
					el.focus();
				}
			}, 100);
		},
		onPasteDescription($event: ClipboardEvent) {
			if (!$event.clipboardData) {
				return;
			}

			$event.preventDefault();
			let t = $event.clipboardData.getData("text");
			this.state.productDescription = t;
			(this.$refs.unitPrice as HTMLDivElement).focus();
		},
		onDescriptionChange() {
			//SkipNewRow is set to false, the product-row is update but no new row added.
			this.add(true);
		},
		insertTimeStamp() {
			console.log("Insert timestamp");
		},
		onKeyPress($event: KeyboardEvent) {
			//ctrl + 5 = timestamp
			if (($event.ctrlKey || $event.metaKey) && $event.keyCode == 53) {
				let target = $event.target as HTMLInputElement;
				let cp = target.selectionStart || 0;
				this.state.productDescription = this.state.productDescription.slice(0, cp)
					+ (this.state.productDescription ? " " : "")
					+ printLongLocalDateTime(new Date())
					+ " " + this.state.productDescription.slice(cp);
			}
		},
		onUnitEnter() {
			requestAnimationFrame(() => {
				(this.$refs.productSku as any).focus();
			});
		},
		onProductSkuEnter() {
			requestAnimationFrame(() => {
				(this.$refs.productDescription as any).focus();
			});
		},
		checkVatRegime() {
			if (this.vatRegime == 2) {
				this.$wf.notify(this.$ct("vat-warning").toString(), "danger", 3000, "exclamation-triangle");
			}
		},
		closeTranslationsModal(){
			this.state.productDescription = this.state.translatedDescription.Fallback || this.state.productDescription;
			this.translatedNamesModal = false;
		}
	},
	mounted() {
		this.$emit("state", this.state);
	},
	components: {
		AppProductSkuAutocomplete,
		AppVatSelect,
		AppUnitAutocomplete,
		AppInputWithTextareaModal,
		Duration,
		AppEditTranslatedNames
	},
	i18nEx: {
		componentPrefix: "components.products-table"
	}
});
